import { useEffect, useMemo, useState } from 'react';
import { Button } from 'foundations-components/transitional/components/Button';
import { MultiColumn, MultiColumnGroup, MultiColumnItem } from 'foundations-components/transitional/components/MultiColumn';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import FormattedMessage from 'I18n/components/FormattedMessage';
import formatName from 'I18n/utils/formatName';
import I18n from 'I18n';
import fuzzy from 'fuzzy';
import { useFetchUsersWithInstallAccess } from '../../hooks/queries/useFetchUsersWithInstallAccess';
import { useApp } from '../../contexts/AppContext';
import { userSettingsUrl } from '../../utils/urls';
import { UserName } from '../UserName';
import { usePaginator } from '../../hooks/usePaginator';
import { useDebounce } from '../../hooks/useDebounce';
import { ITEMS_PER_PAGE, MAX_VISIBLE_PAGE_BUTTONS } from '../../constants/paginator';
import { UserSearchEmptyState } from '../UserSearchEmptyState';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function UserRow({
  user
}) {
  return /*#__PURE__*/_jsx(MultiColumnItem, {
    children: /*#__PURE__*/_jsx(UserName, {
      user: user
    })
  });
}
export function InstallAccessAction({
  isCurrentUser,
  userId
}) {
  if (isCurrentUser) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "app-governance-lib.usersWithInstallAccessDrawer.table.currentUser"
    });
  }
  return /*#__PURE__*/_jsx(Button, {
    use: "tertiary-light",
    size: "xs",
    href: userSettingsUrl(userId),
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "app-governance-lib.usersWithInstallAccessDrawer.table.actions.edit"
    })
  });
}
export function SuperAdminTag() {
  return /*#__PURE__*/_jsx(Tag, {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "app-governance-lib.usersWithInstallAccessDrawer.table.accessSource.superAdmin"
    })
  });
}
export function AppInstallPermissionsTag() {
  return /*#__PURE__*/_jsx(Tag, {
    use: "info",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "app-governance-lib.usersWithInstallAccessDrawer.table.accessSource.appInstallPermissions"
    })
  });
}
function UserAccessRow({
  user,
  AccessTag,
  isCurrentUser
}) {
  return /*#__PURE__*/_jsxs(MultiColumnGroup, {
    children: [/*#__PURE__*/_jsx(UserRow, {
      user: user
    }), /*#__PURE__*/_jsx(MultiColumnItem, {
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "200",
        justifyContent: "end",
        children: [/*#__PURE__*/_jsx(AccessTag, {}), /*#__PURE__*/_jsx(View, {
          flexBasis: "60px",
          textAlign: "center",
          children: /*#__PURE__*/_jsx(InstallAccessAction, {
            isCurrentUser: isCurrentUser,
            userId: user.id
          })
        })]
      })
    })]
  });
}
export function UsersWithInstallAccessTable() {
  const {
    superAdmins,
    integrationsManagementWriteUsers
  } = useFetchUsersWithInstallAccess();
  const {
    currentUserId
  } = useApp();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchQuery = useDebounce(searchInput, 250);
  const handleSearchChange = event => {
    setSearchInput(event.target.value);
  };
  const sortedSuperAdmins = useMemo(() => {
    if (!superAdmins) return [];
    return Object.values(superAdmins).sort((a, b) => {
      if (a.id === currentUserId) {
        return -1;
      }
      if (b.id === currentUserId) {
        return 1;
      }
      return 0;
    });
  }, [superAdmins, currentUserId]);
  const allUsers = useMemo(() => {
    const superAdminUsers = sortedSuperAdmins.map(user => ({
      user,
      AccessTag: SuperAdminTag,
      isCurrentUser: user.id === currentUserId
    }));
    const integrationsUsers = Object.values(integrationsManagementWriteUsers).map(user => ({
      user,
      AccessTag: AppInstallPermissionsTag,
      isCurrentUser: false
    }));
    return [...superAdminUsers, ...integrationsUsers];
  }, [sortedSuperAdmins, integrationsManagementWriteUsers, currentUserId]);
  const filteredUsers = useMemo(() => {
    if (!debouncedSearchQuery) {
      return allUsers;
    }
    const fuzzyResults = fuzzy.filter(debouncedSearchQuery, allUsers, {
      extract: userWithAccess => {
        const {
          user
        } = userWithAccess;
        const formattedName = formatName({
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email
        });
        const searchableText = `${formattedName} ${user.email}`;
        return searchableText;
      }
    });
    return fuzzyResults.map(result => result.original);
  }, [allUsers, debouncedSearchQuery]);
  const {
    currentPage,
    pageCount,
    paginatedItems,
    setPage
  } = usePaginator(filteredUsers, {
    itemsPerPage: ITEMS_PER_PAGE
  });

  // Reset page to 1 when search query changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, setPage]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "400",
      marginBlockEnd: "400",
      marginBlockStart: "600",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h5",
        whiteSpace: "nowrap",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app-governance-lib.installPermissions.options.default.usersTitle",
          options: {
            count: allUsers.length
          }
        })
      }), /*#__PURE__*/_jsx(SearchInput, {
        value: searchInput,
        onChange: handleSearchChange,
        placeholder: I18n.text('app-governance-lib.usersWithInstallAccessDrawer.search.placeholder')
      })]
    }), paginatedItems.length === 0 ? /*#__PURE__*/_jsx(UserSearchEmptyState, {}) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(MultiColumn, {
        children: paginatedItems.map(({
          user,
          AccessTag,
          isCurrentUser
        }) => /*#__PURE__*/_jsx(UserAccessRow, {
          user: user,
          AccessTag: AccessTag,
          isCurrentUser: isCurrentUser
        }, user.id))
      }), pageCount > 1 && /*#__PURE__*/_jsx(Paginator, {
        marginBlockStart: "400",
        page: currentPage,
        pageCount: pageCount,
        maxVisiblePageButtons: MAX_VISIBLE_PAGE_BUTTONS,
        onPageChange: ({
          target: {
            value
          }
        }) => {
          setPage(value);
        }
      })]
    })]
  });
}